import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import BootstrapModal from "react-bootstrap/Modal";
import { Row } from "../types";
import { formatCell, pick, rowText } from "../utils";
import useLang from "Shared/hooks/useLanguage";
import { LoaderIcon } from "Shared/Icons";

interface Props {
  show: boolean;
  rows: Row[];
  onClose: () => void;
  onSave: (dateOfCollection: string) => Promise<void>;
}

// Bulk Edit Date of Collection (v1 EditDOCModal): date can't be after any row's Received Date.
const EditDocModal = ({ show, rows, onClose, onSave }: Props) => {
  const { t } = useLang();
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setDate("");
    setError("");
  }, [show]);

  const invalid = useMemo(
    () =>
      date
        ? rows.filter((r) => {
            const received = pick(r, "ReceivedDate", "ReceiveDate");
            return received && moment(date).isAfter(moment(String(received)), "day");
          })
        : [],
    [rows, date]
  );

  const save = async () => {
    if (!date) return setError(t("Please select Date of Collection"));
    if (invalid.length)
      return setError(
        t("Date of Collection should not be greater than to the Received Date")
      );
    setSaving(true);
    try {
      await onSave(date);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BootstrapModal
      show={show}
      onHide={onClose}
      backdrop="static"
      centered
      dialogClassName="vr2-cm-dialog"
      contentClassName="vr2-cm-content"
    >
      <BootstrapModal.Header closeButton className="py-4 px-6">
        <BootstrapModal.Title className="h5 m-0">
          {t("Bulk Edit Date of Collection")}
          <span className="text-gray-500 fw-normal">
            {" "}
            · {rows.length} {t(rows.length === 1 ? "requisition" : "requisitions")}
          </span>
        </BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body className="px-6 py-4">
        <label className="form-label required">{t("Date of Collection")}</label>
        <input
          type="date"
          className="form-control form-control-sm w-200px mb-4"
          value={date}
          max={moment().format("YYYY-MM-DD")}
          onChange={(e) => {
            setDate(e.target.value);
            setError("");
          }}
          autoFocus
        />
        <div className="text-gray-600 fs-7 mb-2">
          {t("Requisitions selected for the new Date of Collection")}:
        </div>
        <div className="vr2-sc-list">
          {rows.map((r) => {
            const bad = invalid.includes(r);
            return (
              <div
                key={String(pick(r, "__RowKey", "RequisitionOrderId"))}
                className={`vr2-sc-row ${bad ? "text-danger" : ""}`}
              >
                <span className="fw-semibold">{rowText(r, "OrderNumber", "Order")}</span>
                <span>{formatCell(pick(r, "DateofCollection")) || "—"}</span>
                <span className="text-truncate">{rowText(r, "RequisitionType")}</span>
              </div>
            );
          })}
        </div>
        {invalid.length > 0 && (
          <div className="text-danger fs-7 mt-3">
            {t("Invalid Order Numbers")}:{" "}
            {invalid.map((r) => rowText(r, "OrderNumber", "Order")).join(", ")}
          </div>
        )}
        {error && !invalid.length && <div className="text-danger fs-7 mt-2">{error}</div>}
      </BootstrapModal.Body>
      <BootstrapModal.Footer className="py-3 px-6">
        <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={saving || invalid.length > 0}
          onClick={save}
        >
          {saving && <LoaderIcon />} {t("Save")}
        </button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
};

export default EditDocModal;
